import { UIDiv } from '@ui';
import { updatePosition } from '@utils/popoverPositionHelper.js';

export class PopoverClass extends UIDiv {
  constructor({
    trigger,
    content,
    placement = 'bottom',
    hideOnSelect = true,
    hideOnScroll = false,
    onShow = null,
    onHide = null,
    className = '',
  }) {
    super();

    this.setClass('relative');

    this.trigger = trigger;
    this.content = content;
    this.hideOnSelect = hideOnSelect;
    this.hideOnScroll = hideOnScroll;
    this.placement = placement;
    this.onShow = onShow;
    this.onHide = onHide;

    this.popoverContent = new UIDiv();
    this.popoverContent.setClass(
      'bg-white shadow-lg rounded-lg min-w-[150px] opacity-0 hidden transform scale-95 transition-[opacity,transform] duration-200 ease-out focus:outline-none',
    );
    if (className !== '') this.popoverContent.addClass(className);

    if (content) {
      this.popoverContent.add(content);
    }

    document.addEventListener('click', e => {
      if (!this.popoverContent.hasClass('hidden')) {
        // Check if click is outside the popover content
        if (
          !this.popoverContent.dom.contains(e.target) &&
          !this.trigger?.dom?.contains(e.target)
        ) {
          this.hide();
        }
        // Check if click is inside popover content and should hide on select
        else if (
          this.hideOnSelect &&
          this.popoverContent.dom.contains(e.target) &&
          e.target !== this.popoverContent.dom
        ) {
          this.hide();
        }
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !this.popoverContent.hasClass('hidden')) {
        this.hide();
      }
    });

    window.addEventListener(
      'scroll',
      e => {
        if (!this.popoverContent.hasClass('hidden')) {
          if (this.hideOnScroll) {
            if (!this.popoverContent.dom.contains(e.target)) {
              this.hide();
            }
          }
          updatePosition({
            trigger: this.trigger,
            content: this.popoverContent,
            placement: this.placement,
          });
        }
      },
      true,
    );

    window.addEventListener('resize', () => {
      if (!this.popoverContent.hasClass('hidden')) {
        updatePosition({
          trigger: this.trigger,
          content: this.popoverContent,
          placement: this.placement,
        });
      }
    });

    if (this.trigger) {
      this.add(this.trigger);
    }
    document.body.appendChild(this.popoverContent.dom);

    this.popoverContent.onKeyDown(e => {
      const FOCUSABLE_SELECTORS = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ];
      const focusableEls = Array.from(
        this.popoverContent.dom.querySelectorAll(FOCUSABLE_SELECTORS.join(',')),
      );
      const currentIndex = focusableEls.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (focusableEls.length > 0) {
          const nextIndex = (currentIndex + 1) % focusableEls.length;
          focusableEls[nextIndex].focus();
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (focusableEls.length > 0) {
          const prevIndex =
            (currentIndex - 1 + focusableEls.length) % focusableEls.length;
          focusableEls[prevIndex].focus();
        }
      }
    });
  }

  show({ trigger, content, placement, hideOnSelect, hideOnScroll, className }) {
    // If the popover is already open for the same trigger, hide it and stop.
    if (!this.popoverContent.hasClass('hidden') && this.trigger === trigger) {
      this.hide();
      return;
    }
    if (trigger) this.trigger = trigger;
    if (content) {
      this.popoverContent.clear();
      this.content = content;
      this.popoverContent.add(content);
    }
    this.popoverContent.setClass(
      'bg-white shadow-lg rounded-lg min-w-[150px] opacity-0 hidden transform scale-95 transition-[opacity,transform] duration-200 ease-out focus:outline-none',
    );
    if (className && className !== '') this.popoverContent.addClass(className);
    if (placement) this.placement = placement;
    if (hideOnSelect !== undefined) this.hideOnSelect = hideOnSelect;
    if (hideOnScroll !== undefined) this.hideOnScroll = hideOnScroll;

    document.body.appendChild(this.popoverContent.dom);

    if (this.onShow) this.onShow();
    this.popoverContent.removeClass('hidden');

    // For portal positioning, ensure content is positioned before making visible
    this.#positionPortalContent(() => {
      // Apply animation after positioning is complete
      this.popoverContent.setStyle('opacity', ['1']);
      this.popoverContent.setStyle('transform', ['scale(1) translateY(0)']);
      this.popoverContent.setAttribute('tabindex', '-1');
      this.popoverContent.dom.focus();
    });
  }

  #positionPortalContent(callback) {
    // Temporarily position the content off-screen for measurement
    // This prevents the visual "jump" by keeping it invisible during positioning
    this.popoverContent.setStyle('position', ['fixed']);
    this.popoverContent.setStyle('top', ['-9999px']);
    this.popoverContent.setStyle('left', ['-9999px']);
    this.popoverContent.setStyle('opacity', ['0']);
    this.popoverContent.setStyle('visibility', ['visible']); // Allow measurement but keep invisible

    // Use multiple approaches to ensure content is measured
    const attemptPositioning = (attempt = 0) => {
      const contentRect = this.popoverContent.dom.getBoundingClientRect();

      // Check if content has been properly measured
      if ((contentRect.width > 0 && contentRect.height > 0) || attempt >= 10) {
        // Content is ready, apply proper positioning
        updatePosition({
          trigger: this.trigger,
          content: this.popoverContent,
          placement: this.placement,
        });

        // Now make it visible at the correct position
        callback();
      } else {
        // Content not ready, try again
        if (attempt < 5) {
          requestAnimationFrame(() => attemptPositioning(attempt + 1));
        } else {
          // Use setTimeout for remaining attempts
          setTimeout(() => attemptPositioning(attempt + 1), 5);
        }
      }
    };

    // Start the positioning attempt
    requestAnimationFrame(() => attemptPositioning());
  }

  hide() {
    if (this.onHide) this.onHide();
    this.popoverContent.setStyle('opacity', ['0']);
    this.popoverContent.setStyle('transform', ['scale(0.95) translateY(2px)']);
    setTimeout(() => {
      this.popoverContent.addClass('hidden');
    }, 200);

    // Return focus to the trigger element for accessibility
    if (this.trigger) {
      this.trigger.dom.focus();
    }
  }

  destroy() {
    document.body.removeChild(this.popoverContent.dom);
  }
}

const Popover = new PopoverClass({
  trigger: null,
  content: null,
  placement: 'bottom',
  hideOnSelect: true,
  hideOnScroll: false,
  onShow: null,
  onHide: null,
  className: '',
});
export default Popover;
